import React, { useEffect, useState } from "react";
import { Table, Typography, Card, Tag, Space, Divider, Row, Col, Empty } from 'antd';
import { t } from 'i18next';
import Helper from "../../../core/Helper"; // Projenizdeki yol
import {
    MdOutlineMedicalServices,
    MdAccessTime,
    MdConfirmationNumber,
    MdAttachMoney,
    MdFormatListBulleted
} from "react-icons/md";
import { useUser } from "../../../core/context/UserContext";

const { Text, Title } = Typography;

export default function OperationsPreview({ operations, otherOperations }) {
    const { formatPrice } = useUser();
    const [data, setData] = useState([]);
    const [summaries, setSummaries] = useState([]);

    // Ekran genişliğini kontrol etmek için basit bir hook veya css class mantığı kullanacağız.
    // Ancak en temiz yöntem CSS media query ile gizleyip göstermektir.
    // Aşağıdaki style etiketi bu işi görecek.

    const sumPrice = () => {
        const currencies = Array.from(new Set(data.map(x => x.currency)));
        const _summaries = [];
        for (const currency of currencies) {
            const _ops = data.filter(x => x.currency === currency);
            let price = 0;
            for (const op of _ops) {
                price += (op.price === -1 ? 0 : op.price);
            }
            _summaries.push({
                currency,
                price
            });
        }
        setSummaries(_summaries);
    }

    useEffect(() => {
        setData([
            ...operations.map(x => ({ ...x, type: 1 })),
            ...otherOperations.map(x => ({ ...x, type: 2 })),
        ])
    }, [operations, otherOperations]);

    useEffect(() => {
        sumPrice();
    }, [data]);

    // --- RENDER HELPERS ---

    // Fiyat Formatlayıcı
    const renderPrice = (price, currency) => {
        if (price === -1) return <Text type="secondary">-</Text>;
        return <Text strong style={{ color: '#fa8c16' }}>{formatPrice(price, { currency: currency ?? 'TRY' })}</Text>;
    };

    // Alt İşlemleri (SubItems) Gösterme Mantığı
    const renderSubItems = (row) => {
        const subItems = JSON.parse(row?.subItems || '[]');
        if (subItems.length === 0) return null;

        return (
            <div style={{ marginTop: 8, padding: 8, backgroundColor: '#f9f9f9', borderRadius: 6, fontSize: 12 }}>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>{t('Sub Items')}:</Text>
                {subItems.map((item, index) => (
                    <div key={index} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                        <span>• {item?.name}</span>
                        <span>{formatPrice(item?.price, { currency: row?.currency })}</span>
                    </div>
                ))}
            </div>
        );
    };

    // --- TABLE COLUMNS (DESKTOP) ---
    const columns = [
        {
            dataIndex: 'appointmentId',
            key: 'appointmentId',
            title: t('App. ID'),
            width: 90,
            render: (val) => <Tag>{val}</Tag>
        },
        {
            dataIndex: 'startDate',
            key: 'startDate',
            title: t('Date'),
            width: 140,
            render: (val) => <span style={{ fontSize: 13 }}>{new Date(val)?.toLocaleDateString()} <span style={{ color: '#999', fontSize: 11 }}>{new Date(val)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span></span>
        },
        {
            dataIndex: 'name',
            key: 'name',
            title: t('Operation Name'),
            render: (val, row) => {
                const operationName = row.type === 1 ? row.operation?.name : row.name;
                return (
                    <div>
                        <div style={{ fontWeight: 500, color: '#722ed1' }}>{operationName}</div>
                        {renderSubItems(row)}
                    </div>
                );
            }
        },
        {
            dataIndex: 'accessionNumber',
            key: 'accessionNumber',
            title: t('Accession No'),
            width: 200,
            render: (val, row) => row.type === 1 ? <Tag icon={<MdConfirmationNumber style={{ marginRight: 4 }} />} style={{ border: 0, background: 'transparent', padding: 0 }}>{row.accessionNumber}</Tag> : '-'
        },
        {
            dataIndex: 'processMinute',
            key: 'processMinute',
            title: t('Duration'),
            width: 100,
            render: (val, row) => row.type === 1 ? <Space><MdAccessTime color="#8c8c8c" /> {row.processMinute} dk</Space> : '-'
        },
        {
            dataIndex: 'price',
            key: 'price',
            title: t('Price'),
            align: 'right',
            width: 120,
            render: (text, row) => renderPrice(text, row.currency)
        },
    ];

    return (
        <div className="operations-container">
            {/* CSS Styles for Responsive Toggle */}
            <style>{`
                .desktop-table-view { display: block; }
                .mobile-card-view { display: none; }
                
                @media (max-width: 768px) {
                    .desktop-table-view { display: none; }
                    .mobile-card-view { display: block; }
                    .ant-table { font-size: 13px; }
                }
            `}</style>

            {/* --- DESKTOP VIEW: TABLE --- */}
            <div className="desktop-table-view">
                <Table
                    size={'middle'}
                    dataSource={data}
                    rowKey={'id'}
                    pagination={false}
                    columns={columns}
                    bordered
                    scroll={{ x: true }} // Just 'true' is better than fixed pixel usually
                />
            </div>

            {/* --- MOBILE VIEW: CARDS --- */}
            <div className="mobile-card-view">
                {data.length === 0 && <Empty description={t('No Data')} image={Empty.PRESENTED_IMAGE_SIMPLE} />}

                {data.map((row, index) => {
                    const operationName = row.type === 1 ? row.operation?.name : row.name;
                    return (
                        <Card
                            key={index}
                            size="small"
                            style={{ marginBottom: 12, borderRadius: 8, borderLeft: '4px solid #722ed1' }}
                            bodyStyle={{ padding: 12 }}
                        >
                            {/* Header: Name & Price */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <MdOutlineMedicalServices style={{ marginTop: 3, color: '#722ed1' }} />
                                    <Text strong style={{ fontSize: 14, color: '#262626', lineHeight: 1.2 }}>
                                        {operationName}
                                    </Text>
                                </div>
                                <div style={{ textAlign: 'right', minWidth: 80 }}>
                                    {renderPrice(row.price, row.currency)}
                                </div>
                            </div>

                            <Divider style={{ margin: '8px 0' }} dashed />

                            {/* Details Grid */}
                            <Row gutter={[8, 8]} style={{ fontSize: 12 }}>
                                <Col span={12}>
                                    <Space>
                                        <MdAccessTime style={{ color: '#8c8c8c' }} />
                                        <Text type="secondary">{row.type === 1 ? `${row.processMinute} dk` : '-'}</Text>
                                    </Space>
                                </Col>
                                <Col span={12} style={{ textAlign: 'right' }}>
                                    <Space>
                                        <Text type="secondary">{new Date(row.startDate).toLocaleDateString()}</Text>
                                    </Space>
                                </Col>
                                <Col span={24}>
                                    <Space>
                                        <MdConfirmationNumber style={{ color: '#8c8c8c' }} />
                                        <Text type="secondary" copyable>{row.type === 1 ? row.accessionNumber : '-'}</Text>
                                    </Space>
                                </Col>
                            </Row>

                            {/* Sub Items (Mobile) */}
                            {renderSubItems(row)}
                        </Card>
                    );
                })}
            </div>
        </div>
    );
}